import {
  removeAndVerify,
  verifyStoredValue,
} from "./browser-storage-provider.js";

export const FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY =
  "fishystuff.fishydex.legacy-semantic-transition.v1";
export const FISHYDEX_LEGACY_SEMANTIC_TRANSITION_VERSION = 1;

const FIELDS = Object.freeze([
  "operationId",
  "phase",
  "schemaVersion",
  "transition",
]);

function frozen(value) {
  return Object.freeze(structuredClone(value));
}

function boundedOperationId(value) {
  if (
    typeof value !== "string"
    || !value
    || value !== value.trim()
    || value.length > 160
  ) {
    throw new TypeError("Fishydex legacy transition operation ID must be bounded text");
  }
  return value;
}

function invalid(raw, reason) {
  return frozen({ kind: "invalid", raw, reason });
}

export function decodeFishydexLegacySemanticTransition(raw) {
  if (raw === null) {
    return frozen({ kind: "missing", raw: null });
  }
  if (typeof raw !== "string" || raw === "") {
    return invalid(raw, "legacy-transition-must-be-nonempty-json");
  }
  let value;
  try {
    value = JSON.parse(raw);
  } catch (_error) {
    return invalid(raw, "legacy-transition-must-be-json");
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return invalid(raw, "legacy-transition-must-be-an-object");
  }
  if (
    Number.isSafeInteger(value.schemaVersion)
    && value.schemaVersion > FISHYDEX_LEGACY_SEMANTIC_TRANSITION_VERSION
  ) {
    return frozen({
      kind: "unsupportedVersion",
      raw,
      foundVersion: value.schemaVersion,
      reason: "legacy-transition-version-is-unsupported",
    });
  }
  const fields = Object.keys(value).sort();
  if (fields.length !== FIELDS.length || fields.some((field, index) => field !== FIELDS[index])) {
    return invalid(raw, "legacy-transition-fields-must-match-v1");
  }
  if (value.schemaVersion !== FISHYDEX_LEGACY_SEMANTIC_TRANSITION_VERSION) {
    return invalid(raw, "legacy-transition-version-must-be-1");
  }
  try {
    boundedOperationId(value.operationId);
  } catch (_error) {
    return invalid(raw, "legacy-transition-operation-id-must-be-canonical");
  }
  if (value.transition !== "import" && value.transition !== "discard") {
    return invalid(raw, "legacy-transition-kind-must-be-import-or-discard");
  }
  if (value.phase !== "requested" && value.phase !== "acknowledged") {
    return invalid(raw, "legacy-transition-phase-must-be-requested-or-acknowledged");
  }
  if (value.transition === "import" && value.phase !== "acknowledged") {
    return invalid(raw, "legacy-import-transition-must-be-acknowledged");
  }
  return frozen({
    kind: "ready",
    raw,
    receipt: {
      schemaVersion: value.schemaVersion,
      transition: value.transition,
      phase: value.phase,
      operationId: value.operationId,
    },
  });
}

export function fishydexLegacySemanticTransition(storage) {
  return decodeFishydexLegacySemanticTransition(
    storage.getItem(FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY),
  );
}

export function writeFishydexLegacySemanticTransition(storage, value) {
  const raw = JSON.stringify({
    schemaVersion: FISHYDEX_LEGACY_SEMANTIC_TRANSITION_VERSION,
    transition: value.transition,
    phase: value.phase,
    operationId: boundedOperationId(value.operationId),
  });
  const decoded = decodeFishydexLegacySemanticTransition(raw);
  if (decoded.kind !== "ready") {
    throw new TypeError(decoded.reason);
  }
  storage.setItem(FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY, raw);
  verifyStoredValue(storage, FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY, raw);
  return decoded;
}

export function removeFishydexLegacySemanticTransition(storage) {
  removeAndVerify(storage, FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY);
}
